import { Component, inject, Inject, signal, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, EMPTY } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  EmailLabelingConfigComponent,
  EmailLabelingConfigService,
  SaveConfigData,
  MessageCategoryConfig,
} from '@galaxy/email-ui/email-labeling-config';

@Component({
  selector: 'app-email-labeling-settings',
  standalone: true,
  imports: [GalaxyPageModule, TranslateModule, EmailLabelingConfigComponent],
  templateUrl: './email-labeling-settings.component.html',
  styleUrls: ['./email-labeling-settings.component.scss'],
})
export class EmailLabelingSettingsComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(EmailLabelingConfigService);
  private readonly snackbar = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected configId: string | null = null;
  protected readonly existingConfig = signal<MessageCategoryConfig | null>(null);
  protected readonly loading = signal(false);

  protected readonly namespace = toSignal(this.partnerId$, { initialValue: '' });
  protected readonly userId = toSignal(this.userId$, { initialValue: '' });

  constructor(
    @Inject('PARTNER_ID') protected readonly partnerId$: Observable<string>,
    @Inject('USER_ID') protected readonly userId$: Observable<string>,
  ) {
    effect(() => {
      const partnerId = this.namespace();
      if (partnerId && this.configId && this.configId !== 'new') {
        this.loadConfig(this.configId);
      }
    });
  }

  ngOnInit(): void {
    this.configId = this.route.snapshot.paramMap.get('id');
  }

  private loadConfig(configId: string): void {
    this.loading.set(true);

    this.service
      .getConfigById(this.namespace(), configId)
      .pipe(
        tap((config) => this.existingConfig.set(config)),
        catchError(() => {
          this.snackbar.openErrorSnack(this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.LOAD_ERROR'));
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected displayTitle(): string {
    if (this.configId === 'new') {
      return this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.NEW_CONFIGURATION');
    }
    const configName = this.existingConfig()?.name;
    return configName || this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.EDIT_CONFIGURATION');
  }

  protected onLabelsSaved(data: SaveConfigData): void {
    const categories = this.mapLabelFormDataToCategories(data.labels);

    if (this.configId && this.configId !== 'new') {
      this.updateConfig(this.configId, data.configName, categories);
    } else {
      this.createConfig(data.configName, categories);
    }
  }

  private mapLabelFormDataToCategories(labels: SaveConfigData['labels']) {
    return labels.map((label) => ({
      name: label.name,
      color: label.colorHex,
      position: label.position,
      description: label.description || '',
    }));
  }

  private updateConfig(configId: string, configName: string, categories: any[]): void {
    this.loading.set(true);

    this.service
      .updateConfig(this.namespace(), configId, configName, categories)
      .pipe(
        tap(() => {
          this.snackbar.openSuccessSnack(
            this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.UPDATE_SUCCESS'),
          );
          this.router.navigate(['/email-labeling-settings']);
        }),
        catchError(() => {
          this.snackbar.openErrorSnack(this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.UPDATE_ERROR'));
          this.loading.set(false);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private createConfig(configName: string, categories: any[]): void {
    this.loading.set(true);

    this.service
      .createConfig(this.namespace(), configName, categories)
      .pipe(
        tap(() => {
          this.snackbar.openSuccessSnack(
            this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.CREATE_SUCCESS'),
          );
          this.router.navigate(['/email-labeling-settings']);
        }),
        catchError(() => {
          this.snackbar.openErrorSnack(this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.CREATE_ERROR'));
          this.loading.set(false);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected onCancelled(): void {
    this.router.navigate(['/email-labeling-settings']);
  }
}
